// OfftakeBlock — "Port & offtake" panel inside SelectedSiteCard for
// hydrogen / ammonia projects. Renders the offtake Phase 1 payload:
// nearest harbour + alternatives (Kystdatahuset location register),
// maritime activity per vessel class (SafeSeaNet port calls, 3 complete
// years + YTD), and nautical access (pilotage from pilot_boarding_no;
// depth rows carry their pending reason verbatim — nothing is invented).
//
// Reuses the gfb-* classes from GridFeasibilityBlock so the two panels
// read as one system.

import React from 'react';
import { VESSEL_CLASS_LABEL } from '../../config/displaySchema';

const CLASS_LABEL = VESSEL_CLASS_LABEL;

const km = (v) => (v == null ? '—' : `${Number(v).toFixed(v < 10 ? 1 : 0)} km`);
const m  = (v) => (v == null ? '—' : `${Number(v).toFixed(v < 100 ? 1 : 0)} m`);
const nm = (v) => (v == null ? '—' : `${Number(v).toFixed(1)} nm`);

function trendOf(callsPerYear) {
  // first vs last complete year, ±15 % → rising / falling / stable.
  // Mirrors the backend's trend_3yr rule so the label never disagrees
  // with what the API would say.
  const years = Object.keys(callsPerYear || {}).sort();
  if (years.length < 2) return null;
  const a = Number(callsPerYear[years[0]]);
  const b = Number(callsPerYear[years[years.length - 1]]);
  if (!a) return null;
  const d = (b - a) / a;
  return d > 0.15 ? 'rising' : d < -0.15 ? 'falling' : 'stable';
}

export default function OfftakeBlock({ status, data, error, retry, project = null }) {
  if (status === 'idle') return null;

  if (status === 'loading') {
    return (
      <section className="gfb">
        <div className="gfb-head">
          <span className="gfb-eyebrow">Port &amp; offtake</span>
          <span className="gfb-loading">Fetching…</span>
        </div>
      </section>
    );
  }
  if (status === 'error') {
    return (
      <section className="gfb gfb-error">
        <div className="gfb-head">
          <span className="gfb-eyebrow">Port &amp; offtake</span>
          <button type="button" className="gfb-retry" onClick={retry}>retry</button>
        </div>
        <div className="gfb-error-msg">{error || 'Offtake service unavailable.'}</div>
      </section>
    );
  }

  const d    = data || {};
  const port = d.nearest_port;
  const ma   = d.maritime_activity || {};
  const na   = d.nautical_access || {};
  const pil  = na.pilotage || {};
  const ds   = na.depth_sources || {};
  const byClass = Array.isArray(ma.by_class) ? ma.by_class : [];
  const years = ma.window
    ? `${ma.window.start_year_inclusive}–${ma.window.end_year_inclusive}`
    : null;
  const totalCalls = byClass.reduce((s, c) => s + (Number(c.calls_total_3yr) || 0), 0);

  if (!port) {
    return (
      <section className="gfb">
        <div className="gfb-head"><span className="gfb-eyebrow">Port &amp; offtake</span></div>
        <div className="gfb-muted">{d.note || 'No harbour within 15 km.'}</div>
      </section>
    );
  }

  return (
    <section className="gfb">
      <div className="gfb-head">
        <span className="gfb-eyebrow">Port &amp; offtake</span>
        {years && <span className="gfb-sub">SafeSeaNet {years}</span>}
      </div>

      {/* Nearest harbour */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">
          Nearest port with traffic data · {port.name}{port.unlocode ? ` (${port.unlocode})` : ''}
        </div>
        <div className="gfb-grid">
          <div className="gfb-k">Distance</div>
          <div className="gfb-v">{km(port.distance_km)}</div>
          <div className="gfb-k">Alternatives</div>
          <div className="gfb-v">
            {(port.alternatives || []).length === 0
              ? 'None within 15 km'
              : port.alternatives.map(a => `${a.name} ${km(a.distance_km)}`).join(' · ')}
          </div>
        </div>
      </div>

      {/* Register port (Kystverket ports table, from the search enrichment) —
          distinct from the harbour above: this is the row the search
          filtered on, with the (often estimated) depth. */}
      {(() => {
        const e = project?.enrichment || {};
        const name = e.nearest_port_name || e.port_name;
        if (!name) return null;
        const km = e.nearest_port_distance_km ?? e.port_dist_km;
        const depth = e.nearest_port_depth_m ?? e.port_depth_m;
        const est = !!e.nearest_port_depth_estimated;
        return (
          <div className="gfb-panel">
            <div className="gfb-panel-title">Register port · {name}</div>
            <div className="gfb-grid">
              <div className="gfb-k">Distance</div>
              <div className="gfb-v">{km != null ? `${Number(km).toFixed(1)} km` : '—'}</div>
              <div className="gfb-k">Depth</div>
              <div className="gfb-v">{depth != null ? `${depth} m${est ? ' (estimated from port type)' : ''}` : '—'}</div>
            </div>
          </div>
        );
      })()}

      {/* Maritime activity */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">
          Maritime activity{totalCalls ? ` · ${totalCalls.toLocaleString()} calls` : ''}
        </div>
        {byClass.length === 0 ? (
          <div className="gfb-muted">{ma.note || 'No port-call rows for this harbour.'}</div>
        ) : (
          <table className="gfb-queue-table">
            <thead>
              <tr>
                <th className="gfb-queue-name">Vessel class</th>
                {Object.keys(byClass[0].calls_per_year || {}).sort().map(y => (
                  <th key={y} className="gfb-num">{y}</th>
                ))}
                <th className="gfb-num">Trend</th>
                <th className="gfb-num" title="90th percentile ship length in the latest full year">LOA p90{byClass[0]?.latest_year ? ` ${byClass[0].latest_year}` : ''}</th>
                <th className="gfb-num">DWT max</th>
                <th className="gfb-num">Hazmat</th>
              </tr>
            </thead>
            <tbody>
              {[...byClass]
                .sort((a, b) => (b.calls_total_3yr || 0) - (a.calls_total_3yr || 0))
                .map(c => {
                  const yrs = Object.keys(c.calls_per_year || {}).sort();
                  const trend = c.trend_3yr || trendOf(c.calls_per_year);
                  return (
                    <tr key={c.vessel_class}>
                      <td className="gfb-queue-name">{CLASS_LABEL[c.vessel_class] || c.vessel_class}</td>
                      {yrs.map(y => <td key={y} className="gfb-num">{c.calls_per_year[y]}</td>)}
                      <td className="gfb-num">{trend || '—'}</td>
                      <td className="gfb-num">{m(c.latest_year ? c.loa_p90_latest_m : c.loa_p90_m)}</td>
                      <td className="gfb-num">{c.dwt_max != null ? `${Number(c.dwt_max).toLocaleString()} t` : '—'}</td>
                      <td className="gfb-num">{c.dangerous_cargo_calls ?? '—'}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
        {(ma.scope || ma.source?.name) && (
          <div className="gfb-freshness">{[ma.scope, ma.source?.name].filter(Boolean).join(' · ')}</div>
        )}
      </div>

      {/* Nautical access */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">Nautical access</div>
        <div className="gfb-grid">
          <div className="gfb-k">Pilot boarding</div>
          <div className="gfb-v">
            {pil.nearest_boarding_name
              ? `${pil.nearest_boarding_name} · ${nm(pil.boarding_distance_nm)} straight line`
              : '—'}
            {pil.nearest_boarding_name && (
              <div className="gfb-sub">From the site to Kystverket's boarding position, which can lie offshore of the place it is named after; the sailing distance is longer.</div>
            )}
          </div>
          {pil.regime && (<>
            <div className="gfb-k">Pilotage</div>
            <div className="gfb-v">
              {String(pil.regime).replace(/,\s*verify\)/, ')')}
              {/verify/i.test(String(pil.regime)) && (
                <span className="gfb-muted" style={{ marginLeft: 6, fontSize: 10, border: '1px solid #475569', borderRadius: 4, padding: '0 5px' }}>unverified rule</span>
              )}
            </div>
          </>)}
          <div className="gfb-k">Berth depth</div>
          <div className="gfb-v">
            {na.berth_depth_m != null ? m(na.berth_depth_m)
              : <span className="gfb-muted" title={ds.berth_depth?.reason || ''}>pending — no public source yet</span>}
          </div>
          <div className="gfb-k">Controlling depth</div>
          <div className="gfb-v">
            {na.controlling_depth_m != null ? m(na.controlling_depth_m)
              : <span className="gfb-muted" title={ds.controlling_depth?.reason || ''}>pending — Kystverket source temporarily unavailable</span>}
          </div>
        </div>
      </div>
    </section>
  );
}
